"use client";

import { Children, cloneElement, isValidElement, type ReactNode } from "react";
import { Loader2Icon } from "lucide-react";
import { useTranslations } from "next-intl";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DialogTitleBar,
} from "@/components/ui/dialog";
import {
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

export function MultiOptionGroup<T extends string>({
  values,
  options,
  onChange,
  disabled = false,
}: {
  values: T[];
  options: { value: T; label: string }[];
  onChange: (values: T[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = values.includes(option.value);

        return (
          <SettingsActionButton
            key={option.value}
            active={selected}
            requireActive={false}
            hoverNeutral={!selected}
            size="sm"
            disabled={disabled}
            onClick={() => {
              onChange(
                selected
                  ? values.filter((value) => value !== option.value)
                  : [...values, option.value],
              );
            }}
          >
            {option.label}
          </SettingsActionButton>
        );
      })}
    </div>
  );
}

export function OptionGroup<T extends string>({
  value,
  options,
  onChange,
  disabled = false,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = value === option.value;

        return (
          <SettingsActionButton
            key={option.value}
            active={selected}
            requireActive={false}
            hoverNeutral={!selected}
            size="sm"
            disabled={disabled}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </SettingsActionButton>
        );
      })}
    </div>
  );
}

export type RadioOption<T extends string> = {
  value: T;
  label: string;
  example?: string;
};

function preventMouseFocus(event: React.MouseEvent) {
  event.preventDefault();
}

export function RadioOptionGroup<T extends string>({
  name,
  value,
  options,
  onChange,
  direction = "horizontal",
  disabled = false,
}: {
  name: string;
  value: T;
  options: RadioOption<T>[];
  onChange: (value: T) => void;
  direction?: "horizontal" | "vertical";
  disabled?: boolean;
}) {
  return (
    <RadioGroup
      name={name}
      value={value}
      onValueChange={(nextValue) => onChange(nextValue as T)}
      disabled={disabled}
      className={cn(
        direction === "horizontal"
          ? "flex min-h-8 flex-wrap items-center gap-x-4 gap-y-2"
          : "flex flex-col gap-2",
      )}
    >
      {options.map((option) => {
        const id = `${name}-${option.value}`;

        return (
          <div
            key={option.value}
            className={cn(
              "flex items-center gap-2",
              direction === "vertical" && "min-h-8",
            )}
          >
            <RadioGroupItem
              value={option.value}
              id={id}
              onMouseDown={preventMouseFocus}
            />
            <Label
              htmlFor={id}
              className="cursor-pointer font-normal leading-none"
              onMouseDown={preventMouseFocus}

              onClick={(event) => {
                event.preventDefault();
                if (!disabled) {
                  onChange(option.value);
                }
              }}
            >
              {option.label}
              {option.example ? (
                <span className="text-muted-foreground">
                  {" "}
                  （{option.example}）
                </span>
              ) : null}
            </Label>
          </div>
        );
      })}
    </RadioGroup>
  );
}

export function CheckboxOptionGroup<T extends string>({
  name,
  values,
  options,
  onChange,
  direction = "horizontal",
  disabled = false,
  className,
}: {
  name?: string;
  values: T[];
  options: { value: T; label: string }[];
  onChange: (values: T[]) => void;
  direction?: "horizontal" | "vertical";
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div
      data-slot="checkbox-group"
      className={cn(
        direction === "horizontal"
          ? "flex min-h-8 flex-wrap items-center gap-x-4 gap-y-2"
          : "flex flex-col gap-2",
        className,
      )}
    >
      {options.map((option) => {
        const checked = values.includes(option.value);
        const id = name ? `${name}-${option.value}` : `checkbox-${option.value}`;

        return (
          <div
            key={option.value}
            className={cn(
              "flex items-center gap-2",
              direction === "vertical" && "min-h-8",
            )}
          >
            <Checkbox
              id={id}
              checked={checked}
              disabled={disabled}
              onMouseDown={preventMouseFocus}
              onCheckedChange={(nextChecked) => {
                onChange(
                  nextChecked
                    ? [...values, option.value]
                    : values.filter((item) => item !== option.value),
                );
              }}
            />
            <Label
              htmlFor={id}
              className="cursor-pointer font-normal leading-none"
              onMouseDown={preventMouseFocus}
            >
              {option.label}
            </Label>
          </div>
        );
      })}
    </div>
  );
}

export const settingsTableLabelClass =
  "whitespace-nowrap text-sm font-medium leading-snug text-foreground/90";

export function SettingTable({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div data-slot="setting-table" className={cn("table w-full border-collapse", className)}>
      <div className="table-row-group">{children}</div>
    </div>
  );
}

export function SettingTableDescription({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return (
    <p
      className={cn("text-xs leading-relaxed text-muted-foreground", className)}
      {...props}
    />
  );
}

const settingTableCellBorderClass = "border-b border-border/40";

export function SettingTableRow({
  label,
  children,
  description,
  className,
  labelClassName,
  htmlFor,
  align,
  nested = false,
}: {
  label: ReactNode;
  children: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  labelClassName?: string;
  htmlFor?: string;

  align?: "center" | "top";
  nested?: boolean;
}) {
  const hasDescription = description != null && description !== "";
  const centerControls = (align ?? "center") === "center";
  const cellBgClass = nested ? "bg-muted/25" : undefined;
  const rowAlignClass = centerControls && !hasDescription ? "align-middle" : "align-top";

  return (
    <div
      data-slot="setting-table-row"
      className={cn("table-row", !centerControls && "[&:has(>div>div>textarea[data-slot=textarea])>label]:pt-[19px]", className)}
    >
      <Label
        htmlFor={htmlFor}
        className={cn(
          "table-cell py-2.5 pr-4",
          settingTableCellBorderClass,
          cellBgClass,
          settingsTableLabelClass,
          rowAlignClass,
          !centerControls && "leading-5",
          labelClassName,
        )}
      >
        {centerControls && hasDescription ? (
          <span className="flex h-8 items-center">{label}</span>
        ) : (
          label
        )}
      </Label>
      <div
        className={cn(
          "table-cell w-full min-w-0 py-2.5",
          settingTableCellBorderClass,
          cellBgClass,
          rowAlignClass,
          hasDescription && "space-y-1.5",
        )}
      >
        <div
          className={cn(centerControls && "flex min-h-8 items-center")}
        >
          {children}
        </div>
        {hasDescription ? (
          <SettingTableDescription>{description}</SettingTableDescription>
        ) : null}
      </div>
    </div>
  );
}

export const settingsTableFormClass = "divide-y divide-border/50";

export function isSingleLineText(value: string) {
  return !/[\r\n]/.test(value);
}

export function settingsTextareaLabelClass(
  value: string,
  baseClassName?: string,
) {
  return cn(
    baseClassName,
    isSingleLineText(value)
      ? "mt-2 pt-0 leading-none"
      : "pt-0.5 leading-snug",
  );
}

function nestSettingTableRows(children: React.ReactNode): React.ReactNode {
  return Children.map(children, (child) => {
    if (!isValidElement<{ nested?: boolean; children?: React.ReactNode }>(child)) {
      return child;
    }

    if (child.type === SettingTableRow) {
      return cloneElement(child, { nested: true });
    }

    if (child.props.children) {
      return cloneElement(child, {
        children: nestSettingTableRows(child.props.children),
      });
    }

    return child;
  });
}

export function ToggleField({
  label,
  name,
  enabled,
  onEnabledChange,
  children,
  className,
  labelClassName,
}: {
  label: string;
  name: string;
  enabled: boolean;
  onEnabledChange: (value: boolean) => void;
  children?: React.ReactNode;
  className?: string;
  labelClassName?: string;
}) {
  return (
    <>
      <SettingTableRow
        label={label}
        labelClassName={labelClassName}
        align="center"
        className={className}
      >
        <Switch
          id={name}
          checked={enabled}
          onMouseDown={preventMouseFocus}
          onCheckedChange={onEnabledChange}
        />
      </SettingTableRow>
      {enabled && children ? nestSettingTableRows(children) : null}
    </>
  );
}

export function SwitchField({
  label,
  name,
  checked,
  onCheckedChange,
  className,
  labelClassName,
}: {
  label: string;
  name: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  className?: string;
  labelClassName?: string;
}) {
  return (
    <SettingTableRow
      label={label}
      labelClassName={labelClassName}
      align="center"
      className={className}
    >
      <Switch
        id={name}
        checked={checked}
        onMouseDown={preventMouseFocus}
        onCheckedChange={onCheckedChange}
      />
    </SettingTableRow>
  );
}

export const settingsFieldsetClass =
  "min-w-0 rounded-lg border border-border/80 px-4 pb-3 pt-0";

export const settingsFieldsetLegendClass =
  "px-1.5 text-sm font-medium text-foreground";

export function SettingsFieldSection({
  legend,
  children,
  className,
  contentClassName,
  table = true,
}: {
  legend: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;

  table?: boolean;
}) {
  return (
    <FieldSet className={cn(settingsFieldsetClass, className)}>
      <FieldLegend variant="legend" className={settingsFieldsetLegendClass}>
        {legend}
      </FieldLegend>
      <div className={cn("pt-2", contentClassName)}>
        {table ? <SettingTable>{children}</SettingTable> : children}
      </div>
    </FieldSet>
  );
}

export const settingsFormGridClass = "grid gap-5 md:grid-cols-2";

export const settingsSaveBarStickyClass =
  "sticky bottom-0 z-50 flex shrink-0 justify-end border-t border-border/60 bg-card px-5 py-4";

export function SettingsSaveBar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div data-admin-save-bar className={cn(settingsSaveBarStickyClass, className)}>
      {children}
    </div>
  );
}

const settingsActionInactiveClass =
  "border-border/60 bg-muted/20 text-[#999] shadow-none dark:text-[#999]";

const settingsSoftPrimaryClass =
  "border-blue-200 bg-blue-50 text-blue-600 shadow-none hover:border-blue-400 hover:bg-blue-100 hover:text-blue-700 dark:border-blue-800/80 dark:bg-blue-950/40 dark:text-blue-400 dark:hover:border-blue-600 dark:hover:bg-blue-950/60 dark:hover:text-blue-300";

const settingsSoftDestructiveClass =
  "border-rose-200 bg-rose-50 text-rose-600 shadow-none hover:border-rose-400 hover:bg-rose-100 hover:text-rose-700 dark:border-rose-800/80 dark:bg-rose-950/40 dark:text-rose-400 dark:hover:border-rose-600 dark:hover:bg-rose-950/60 dark:hover:text-rose-300";

const settingsSoftDestructiveHoverClass =
  "hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 dark:hover:border-rose-800 dark:hover:bg-rose-950/40 dark:hover:text-rose-400";

const settingsSoftPrimaryHoverClass =
  "hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 dark:hover:border-blue-800 dark:hover:bg-blue-950/40 dark:hover:text-blue-400";

export const settingsSoftNeutralClass =
  "border-neutral-400 bg-[#efefef] text-neutral-700 shadow-none hover:border-neutral-500 hover:bg-[#e5e5e5] hover:text-neutral-800 dark:border-neutral-600/80 dark:bg-neutral-950/40 dark:text-neutral-300 dark:hover:border-neutral-500 dark:hover:bg-neutral-900/60 dark:hover:text-neutral-100";

export const settingsSoftNeutralHoverClass =
  "hover:border-neutral-500 hover:bg-[#efefef] hover:text-neutral-700 dark:hover:border-neutral-600 dark:hover:bg-neutral-950/40 dark:hover:text-neutral-300";

export type SettingsActionTone = "neutral" | "primary" | "destructive";
export type SettingsActionVariant = "plain" | "outline" | "soft" | "mutedOutline";

const settingsToneClasses: Record<SettingsActionTone, string> = {
  neutral: settingsSoftNeutralClass,
  primary: settingsSoftPrimaryClass,
  destructive: settingsSoftDestructiveClass,
};

const settingsOutlineSurfaces: Record<SettingsActionTone, string> = {
  neutral: "bg-transparent hover:bg-[#efefef] dark:bg-transparent dark:hover:bg-neutral-950/40",
  primary: "bg-transparent hover:bg-blue-50 dark:bg-transparent dark:hover:bg-blue-950/40",
  destructive: "bg-transparent hover:bg-rose-50 dark:bg-transparent dark:hover:bg-rose-950/40",
};

function settingsActionAppearance(tone: SettingsActionTone, variant: SettingsActionVariant) {
  return cn(
    settingsToneClasses[tone],
    variant === "plain" && "bg-transparent hover:bg-transparent dark:bg-transparent dark:hover:bg-transparent",
    variant === "outline" && settingsOutlineSurfaces[tone],
  );
}

export function SettingsActionButton({
  active,
  tone,
  variant,
  saving = false,
  requireActive = true,
  hoverDestructive = false,
  hoverPrimary = false,
  hoverNeutral = false,
  neutral = false,
  disabled: disabledProp = false,
  type = "button",
  onClick,
  className,
  children,
  size,
  render,
  nativeButton,
}: {
  active: boolean;

  tone?: SettingsActionTone;

  variant?: SettingsActionVariant;
  saving?: boolean;
  requireActive?: boolean;
  hoverDestructive?: boolean;
  hoverPrimary?: boolean;
  hoverNeutral?: boolean;

  neutral?: boolean;
  disabled?: boolean;
  type?: "submit" | "button";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
  children?: React.ReactNode;
  size?: React.ComponentProps<typeof Button>["size"];
  render?: React.ReactElement;

  nativeButton?: boolean;
}) {
  const t = useTranslations("site.ui");
  const disabled = disabledProp || saving || (requireActive && !active);
  const explicitAppearance = tone !== undefined || variant !== undefined;
  const resolvedTone = tone ?? (neutral || hoverNeutral ? "neutral" : hoverDestructive ? "destructive" : "primary");
  const resolvedVariant = variant ?? "soft";
  const appearanceClass = explicitAppearance
    ? !active && requireActive
      ? cn(settingsActionInactiveClass, resolvedVariant !== "soft" && "bg-transparent dark:bg-transparent")
      : settingsActionAppearance(resolvedTone, resolvedVariant)
    : cn(
        !active && settingsActionInactiveClass,
        active && neutral && settingsSoftNeutralClass,
        active && !neutral && !hoverDestructive && !hoverPrimary && settingsSoftPrimaryClass,
        active && hoverDestructive && settingsSoftDestructiveClass,
        !active && hoverDestructive && !disabled && settingsSoftDestructiveHoverClass,
        !active && hoverPrimary && !disabled && settingsSoftPrimaryHoverClass,
        !active && hoverNeutral && !disabled && settingsSoftNeutralHoverClass,
      );

  return (
    <Button
      type={type}
      size={size}
      variant={resolvedVariant === "mutedOutline" ? "mutedOutline" : "secondary"}
      tone={resolvedTone}
      disabled={disabled}
      render={render}
      nativeButton={nativeButton}
      aria-busy={saving}
      className={cn(
        "transition-colors duration-200",
        requireActive && !active && "disabled:opacity-100",
        resolvedVariant !== "mutedOutline" && appearanceClass,
        className,
      )}
      onClick={onClick}
    >
      {saving ? (
        <>
          <Loader2Icon className="size-3.5 animate-spin" aria-hidden />
          <span className="sr-only">{t("saving")}</span>
        </>
      ) : (
        children
      )}
    </Button>
  );
}

export function DialogCancelButton({
  onClick,
  variant = "plain",
  saving = false,
  disabled = false,
  className,
  children,
  type = "button",
}: {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: SettingsActionVariant;
  saving?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  type?: "submit" | "button";
}) {
  const t = useTranslations("site.ui");

  return (
    <SettingsActionButton
      active
      tone="neutral"
      variant={variant}
      requireActive={false}
      saving={saving}
      disabled={disabled}
      type={type}
      onClick={onClick}
      className={className}
    >
      {children ?? t("cancel")}
    </SettingsActionButton>
  );
}

export function DialogDeleteButton({
  onClick,
  variant = "soft",
  saving = false,
  disabled = false,
  className,
  children,
  confirmLabel,
  deletingLabel,
  type = "button",
}: {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: SettingsActionVariant;
  saving?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  confirmLabel?: string;
  deletingLabel?: string;
  type?: "submit" | "button";
}) {
  const t = useTranslations("site.ui");

  return (
    <SettingsActionButton
      active
      tone="destructive"
      variant={variant}
      requireActive={false}
      saving={saving}
      disabled={disabled}
      type={type}
      onClick={onClick}
      className={className}
    >
      {children ??
        (saving ? (deletingLabel ?? t("deleting")) : (confirmLabel ?? t("confirmDelete")))}
    </SettingsActionButton>
  );
}

export function DialogConfirmButton({
  onClick,
  variant = "soft",
  saving = false,
  disabled = false,
  active = true,
  requireActive = false,
  className,
  children,
  type = "button",
}: {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: SettingsActionVariant;
  saving?: boolean;
  disabled?: boolean;
  active?: boolean;
  requireActive?: boolean;
  className?: string;
  children?: React.ReactNode;
  type?: "submit" | "button";
}) {
  const t = useTranslations("site.ui");

  return (
    <SettingsActionButton
      active={active}
      tone="primary"
      variant={variant}
      requireActive={requireActive}
      saving={saving}
      disabled={disabled}
      type={type}
      onClick={onClick}
      className={className}
    >
      {children ?? t("confirm")}
    </SettingsActionButton>
  );
}

export function SettingsSaveButton({
  dirty,
  variant = "soft",
  saving = false,
  type = "submit",
  onClick,
  className,
  children,
}: {
  dirty: boolean;
  variant?: SettingsActionVariant;
  saving?: boolean;
  type?: "submit" | "button";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
  children?: React.ReactNode;
}) {
  const t = useTranslations("site.ui");

  return (
    <SettingsActionButton
      active={dirty}
      tone="primary"
      variant={variant}
      saving={saving}
      type={type}
      onClick={onClick}
      className={className}
    >
      {children ?? t("save")}
    </SettingsActionButton>
  );
}

export const settingsTabHeaderClass =
  "sticky top-0 z-50 shrink-0 border-b border-border/60 bg-card px-5";

export const settingsTabListClass =
  "!h-auto !min-h-0 w-fit items-stretch gap-4 -mb-px rounded-none border-0 bg-transparent p-0";

export const settingsTabTriggerClass =
  "relative box-border !h-auto flex-none rounded-none border-0 border-b-2 border-transparent bg-transparent px-2.5 py-4 text-sm leading-none font-normal text-muted-foreground shadow-none transition-colors duration-200 hover:text-primary data-active:font-medium data-active:text-primary after:!hidden";

export const settingsTabSaveBarClass =
  "flex shrink-0 justify-end border-t border-border/60 px-5 py-4";

export const settingsTabsCardClass =
  "flex min-h-[calc(100svh-var(--admin-header-height)-var(--admin-content-inset))] shrink-0 flex-col overflow-hidden !py-0";

export function SettingsTabsLoading() {
  const t = useTranslations("site.ui");

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 py-16">
      <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{t("loading")}</p>
    </div>
  );
}

export const settingsTabsCardCompactClass =
  "flex shrink-0 flex-col overflow-hidden !py-0";

export function SettingsHashTabsCard({
  embedded = false,
  isReady,
  children,
  className,
  compact = false,
  loadingFallback,
}: {
  isReady: boolean;
  children: React.ReactNode;
  className?: string;
  compact?: boolean;
  embedded?: boolean;
  loadingFallback?: React.ReactNode;
}) {
  if (embedded) return <div className={cn("flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden", className)}>{isReady ? children : loadingFallback ?? <SettingsTabsLoading />}</div>;
  return (
    <Card
      data-admin-page-card
      className={cn(
        compact ? settingsTabsCardCompactClass : settingsTabsCardClass,
        className,
      )}
    >
      {isReady ? children : loadingFallback ?? <SettingsTabsLoading />}
    </Card>
  );
}

export const settingsTabsRootClass = "flex min-h-0 flex-1 flex-col gap-0";

export const settingsTabPanelClass =
  "mt-0 flex min-h-0 flex-1 flex-col outline-none";

export const settingsEmbeddedFormClass = "flex min-h-0 flex-1 flex-col";

export const settingsEmbeddedFormShellClass =
  "flex min-h-0 flex-1 flex-col [&>*:last-child]:mt-auto [&>*:last-child]:shrink-0";

export const settingsTabPanelEmbeddedClass = "px-5 py-4";

export const settingsEmbeddedScrollClass =
  "min-h-0 flex-1 overflow-y-auto overflow-anchor-none";

export function SettingsEmbeddedScroll({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        settingsEmbeddedScrollClass,
        settingsTabPanelEmbeddedClass,
        className,
      )}
    >
      {children}
    </div>
  );
}

export const settingsCardContentClass = "pt-0";

export const settingsTabPanelFirstCardClass =
  "[&>fieldset:first-child]:mt-4";

export const settingsTabPanelLastCardClass =
  "[&>fieldset:last-child]:mb-4";

export const settingsTabPanelContentClass = "pt-4";

export function SettingsSectionHeader({
  title,
  description,
  className,
}: {
  title: string;
  description?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "space-y-0.5 border-t border-border/60 pt-4",
        className,
      )}
    >
      <h2 className="text-sm font-medium tracking-tight">{title}</h2>
      {description ? (
        <p className="text-xs text-muted-foreground/65">{description}</p>
      ) : null}
    </div>
  );
}

export function SettingsFieldset({
  legend,
  children,
  className,
  contentClassName,
}: {
  legend: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <SettingsFieldSection
      legend={legend}
      className={className}
      contentClassName={cn("space-y-5", contentClassName)}
    >
      {children}
    </SettingsFieldSection>
  );
}

export function shouldUseSettingsFieldset(sectionCount: number) {
  return sectionCount > 2;
}

export function SettingsSection({
  legend,
  sectionCount,
  children,
  className,
  contentClassName,
}: {
  legend: React.ReactNode;
  sectionCount: number;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  if (!shouldUseSettingsFieldset(sectionCount)) {
    return (
      <div className={cn("space-y-5", contentClassName, className)}>
        {children}
      </div>
    );
  }

  return (
    <SettingsFieldset
      legend={legend}
      className={className}
      contentClassName={contentClassName}
    >
      {children}
    </SettingsFieldset>
  );
}

export function AdminDialogHeader({
  title,
  subtitle,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  className?: string;
}) {
  return (
    <DialogTitleBar
      data-admin-dialog-header=""
      title={title}
      subtitle={subtitle}
      className={className}
    />
  );
}
